import React, {Fragment, useEffect, useMemo, useState} from 'react'
import {useAsync} from 'react-async-hook'
import {useTranslation} from 'react-i18next'
import {useRouteMatch} from 'react-router-dom'
import {
  Button,
  IconButton,
  makeStyles,
  MenuItem,
  OutlinedInput,
  Select,
} from '@material-ui/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import ReplayIcon from '@material-ui/icons/Replay'
import Logo from 'pages/HealthCenter/Report/Elements/logo.svg'
import HealthCenterReportStyle from 'pages/HealthCenter/Report/Style'
import MCIReportPageStage5 from 'components/molcules/MciReport/MCIReportPageStage5'
import MCIReportPageStage5API from 'components/molcules/MciReport/MCIReportPageStage5API'
import MCIReportPageStage8API from 'components/molcules/MciReport/MCIReportPageStage8API'
import Header from 'pages/HealthCenter/Report/Elements/Header'
import IconDownload from 'pages/HealthCenter/Report/Elements/IconDownload.svg'
import {
  ReportWrap,
  ReportFetcherWrap,
  ReportCustomPadding,
} from 'pages/Analysis/EEGIndividual/Report'
import {Locale} from 'components/atoms/TranslationSelect'
import useLanguageReport from 'hooks/useLanguageReport'
import useLanguage from 'features/languageReport/useLanguage'
import {
  createMciAnalysisPDFReport,
  fetchMciReportApi,
  FetchMCIReportRequest,
  getMciAnalysisReport,
  MCIAnalysisReportResponse,
  MCIReport,
} from 'api/analysisApi'
import {INIT_MCI_ANALYSIS_REPORT} from 'constants/AnalysisConstant'
import {getAccessToken} from 'api/storageApi'
import {downloadFileFromWsUrl} from 'helpers/fileHelper'
import HeaderMultiLangReport from 'pages/HealthCenter/Report/Elements/HeaderMultiLang'
import {ResEEGMCI8Stages} from 'api/languageApi'

const useStyles = makeStyles(HealthCenterReportStyle)

function ReportPage() {
  const {
    params: {id},
  } = useRouteMatch<{id: string}>()
  const {t, i18n} = useTranslation()
  const locale = i18n.language
  const {textHealthAPI} = useLanguage('health')
  const {textMciAPI} = useLanguage('mci')
  const [loadingDownload, setLoadingDownload] = useState(false)
  const [currentLocale, setCurrentLocale] = useState<Locale>('ko')

  const params: FetchMCIReportRequest = {
    requestIds: [Number.parseInt(id, 10)],
    locale,
  }

  const handleChangeLang = async (value: Locale) => {
    // setLanguage(value)
    await i18n.changeLanguage(value)
    setCurrentLocale(value)
  }

  const LANG = [
    {
      label: t('IKoranLang'),
      value: 'ko',
    },
    {
      label: t('IEnglishLang'),
      value: 'en',
    },
    {
      label: t('IJapanesesLang'),
      value: 'ja',
    },
    {
      label: t('ISpanishLang'),
      value: 'es',
    },
    {
      label: t('IRomanianLang'),
      value: 'ro',
    },
    {
      label: t('IThaiLang'),
      value: 'th',
    },
    {
      label: t('IChineseLang'),
      value: 'zh',
    },
  ]

  const asyncResult = useAsync(fetchMciReportApi, [params], {
    executeOnMount: true,
    executeOnUpdate: false,
  })

  const [resultInfo, setResultInfo] = useState<MCIReport>()
  const [analysis, setAnalysis] = useState<MCIAnalysisReportResponse>(
    INIT_MCI_ANALYSIS_REPORT,
  )

  const getMCIAnalysisReportFromApi = async () => {
    const response = await getMciAnalysisReport(Number(id), locale)
    setAnalysis(response)
  }

  const handleDownloadPDFReport = async () => {
    try {
      setLoadingDownload(true)
      const createResponse = await createMciAnalysisPDFReport(
        [Number.parseInt(id, 10)],
        i18n.language,
      )
      await downloadFileFromWsUrl(createResponse.ws_url, getAccessToken() ?? '')
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingDownload(false)
    }
  }

  const classes = useStyles()

  const analysisMCISteps = (() => {
    if (!textMciAPI) return 0

    const threshold =
      'mci' in textMciAPI ? textMciAPI.mci?.threshold : textMciAPI.threshold

    return Object.keys(threshold || {}).filter((item) => item !== 'type').length
  })()

  const TAB = [
    {
      index: 0,
      name: t('IAIDementiaPredictionAnalysisTitle', {
        stage: analysisMCISteps,
      }),
    },
  ]

  const titleHeader = useMemo(() => {
    if (textHealthAPI?.header) {
      return textHealthAPI?.header
    }
    return null
  }, [textHealthAPI])

  useEffect(() => {
    if (asyncResult.result?.list) {
      const [result] = asyncResult.result.list
      setResultInfo(result)
    }
  }, [asyncResult.result])

  useEffect(() => {
    setCurrentLocale(locale as Locale)
    asyncResult.execute(params)
    getMCIAnalysisReportFromApi()
  }, [locale])

  const {isLocalLanguage} = useLanguageReport('mci', analysis.version)
  useLanguageReport('health')

  return (
    <Fragment>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row-reverse',
        }}
      >
        <div className={`${classes.topNav} no-print`} style={{width: '100%'}}>
          <div className={classes.topNavLeft}>
            <div className={classes.logo}>
              <img src={Logo} alt='Logo' />
            </div>
            <div className={classes.tabs}>
              {TAB.map((tab) => (
                <div
                  className={classes.tab}
                  aria-hidden='true'
                  key={tab.index}
                  style={{
                    color: '#00A29C',
                    fontWeight: 'bold',
                  }}
                >
                  {t(tab.name)}
                </div>
              ))}
            </div>
          </div>
          <div className={classes.topNavRight}>
            <div>
              <Select
                value={currentLocale}
                onChange={(e) => handleChangeLang(e.target.value as Locale)}
                className={classes.searchSelect}
                input={<OutlinedInput name='age' id='outlined-age-simple' />}
              >
                {LANG.map((item) => (
                  <MenuItem value={item.value} key={item.value}>
                    {t(item.label)}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <Button
              disabled={loadingDownload}
              onClick={handleDownloadPDFReport}
            >
              <img src={IconDownload} alt='IconDownload' />
            </Button>
          </div>
        </div>
      </div>
      <div
        style={{
          width: '1300px',
        }}
        id='print-modified'
      >
        <ReportWrap style={{width: '100%'}}>
          <ReportFetcherWrap style={{width: '100%'}}>
            <ReportCustomPadding />
            <div
              style={{
                minHeight: 'calc(100vh - 91px - 77px - 77px - 15px)',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {asyncResult.loading && (
                <CircularProgress size='3rem' color='secondary' />
              )}
              {asyncResult.error && (
                <div>
                  {asyncResult.error.message}
                  <IconButton
                    onClick={() => asyncResult.execute(params)}
                    size='small'
                  >
                    <ReplayIcon />
                  </IconButton>
                </div>
              )}
              {asyncResult.result && (
                <div style={{width: '100%'}}>
                  {resultInfo !== undefined &&
                    (isLocalLanguage ? (
                      <Header
                        title={TAB[0].name}
                        content=''
                        name={t('IGetFullName', {
                          firstName: resultInfo.clientFirstName ?? '',
                          lastName: resultInfo.clientLastName ?? '',
                        })}
                        sex={resultInfo.gender}
                        birthday={resultInfo.birth.replaceAll('-', '.')}
                        measurementDate={resultInfo.measureDate}
                      />
                    ) : (
                      <HeaderMultiLangReport
                        title={TAB[0].name}
                        tabIndex={0}
                        content=''
                        name={t('IGetFullName', {
                          firstName: resultInfo.clientFirstName ?? '',
                          lastName: resultInfo.clientLastName ?? '',
                        })}
                        sex={resultInfo.gender}
                        birthday={resultInfo.birth}
                        measurementDate={resultInfo.measureDate}
                        titleHeader={titleHeader}
                      />
                    ))}
                  {resultInfo !== undefined && (
                    <div
                      style={{
                        paddingBottom: 100,
                        background: 'white',
                        paddingLeft: 57,
                        paddingRight: 57,
                      }}
                    >
                      {analysisMCISteps === 5 &&
                        (isLocalLanguage ? (
                          <MCIReportPageStage5
                            MciImagePath={analysis.images.mci_aging_curve}
                            probability={analysis.scores.probability}
                            clientFirstName={resultInfo.clientFirstName ?? ''}
                            clientLastName={resultInfo.clientLastName ?? ''}
                            footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                          />
                        ) : (
                          <MCIReportPageStage5API
                            MciImagePath={analysis.images.mci_aging_curve}
                            probability={analysis.scores.probability}
                            clientFirstName={resultInfo.clientFirstName ?? ''}
                            clientLastName={resultInfo.clientLastName ?? ''}
                            footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                          />
                        ))}
                      {analysisMCISteps === 8 && (
                        <MCIReportPageStage8API
                          MciImagePath={analysis.images.mci_aging_curve}
                          probability={analysis.scores.probability}
                          clientFirstName={resultInfo.clientFirstName ?? ''}
                          clientLastName={resultInfo.clientLastName ?? ''}
                          textMciAPI={textMciAPI as ResEEGMCI8Stages}
                          footer={textHealthAPI?.common?.footer?.[0] ?? ''}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <ReportCustomPadding />
          </ReportFetcherWrap>
          <ReportCustomPadding />
        </ReportWrap>
      </div>
    </Fragment>
  )
}

export default ReportPage
